import React from 'react';
import {Platform, StyleSheet, View} from 'react-native';
import {observer} from 'mobx-react-lite';

import {useResolvedPricing} from '../../../hooks/pricing';
import {useWebScreenDimensions} from '../../../hooks/useWebScreenDimensions';
import {AppRoute} from '../../../models/AppRoute';
import useAppNavigation from '../../../navigation/useAppNavigation';
import theme from '../../../style/theme';
import {OrderLine} from '../../../transport/apiServiceResponse';
import {toAUD} from '../../../utils/functions';
import {isGiftCardProduct} from '../../../utils/giftCards';
import {makeByLineText} from '../../../utils/products';
import {buildProductTestId} from '../../../utils/string';
import Text from '../../ui-controls/Text';
import ProductTileActions from '../primitives/ProductTileActions';
import ProductTileContainer from '../primitives/ProductTileContainer';
import ProductTileImage from '../primitives/ProductTileImage';
import {COLORS, TILE_DIMENSIONS, TYPOGRAPHY} from '../styles';
import {ProductTileVariantProps} from '../types';

type ProductTileHorizontalProps = ProductTileVariantProps & {
  showPreviousQuantity?: boolean;
  pressCallback?: (item: OrderLine) => void;
  /** When true, disables tile press to prevent double-clicks during cart operations */
  isProcessing?: boolean;
};

const MEMBER_SIDEBAR_TEXT = {
  public: 'MEMBERS PRICE',
  private: 'JUST FOR YOU',
} as const;

/**
 * Horizontal product tile for abbreviated layouts (Search, Wishlist, OrderView).
 * Shows image on left (80x80), name/byline/price/cart button on right.
 *
 * Member pricing: yellow left sidebar with rotated label text + yellow border
 * around entire tile. Dual inline pricing with "Member"/"Non-members" tags.
 * Responsive: single price row on desktop, wraps to two rows on mobile.
 */
const ProductTileHorizontal = observer(function ProductTileHorizontal({
  item,
  onPress,
  pressCallback,
  showPreviousQuantity = false,
  testContext,
  disabled = false,
  containerStyle,
  isProcessing = false,
}: ProductTileHorizontalProps) {
  const navigation = useAppNavigation();
  const {isWebDesktopScreen} = useWebScreenDimensions();
  const testID = buildProductTestId(item.name, testContext);
  const pricing = useResolvedPricing(item, 'marketing');
  // DIS-1590: the tile advertises the pair wherever the store honours a member
  // price; it is not a claim about this shopper. Require nonMemberPrice so we never
  // paint a dual row with `$--` when extras are partial (CartLineItem does the same).
  const hasMemberPricing =
    pricing.advertiseMemberPrice && pricing.memberPrice !== null && pricing.nonMemberPrice !== null;
  // Mobile member tiles: stack pricing above button to prevent text overlap
  const stackedLayout = hasMemberPricing && !isWebDesktopScreen;

  const handlePress = () => {
    if (isProcessing) return;

    if (pressCallback) {
      pressCallback(item);
    } else if (onPress) {
      onPress(item);
    } else {
      // @ts-expect-error - Navigation type is complex with nested screens
      navigation.navigate(AppRoute.ROOT, {
        screen: AppRoute.MAIN,
        params: {
          screen: AppRoute.PRODUCT,
          params: {product: item},
        },
      });
    }
  };

  const byline = makeByLineText(item);
  const isGiftCard = isGiftCardProduct(item);

  return (
    <ProductTileContainer
      item={item}
      onPress={handlePress}
      disabled={disabled || isProcessing}
      containerStyle={[
        styles.container,
        hasMemberPricing ? styles.containerMember : null,
        containerStyle,
      ]}
      testID={testID}>
      {/* Yellow left sidebar with rotated label for member-priced items */}
      {hasMemberPricing && pricing.membershipType ? (
        <View
          style={[
            styles.memberSidebar,
            pricing.membershipType === 'private' ? styles.memberSidebarPrivate : null,
          ]}
          testID={`${testID}-member-sidebar`}>
          <Text style={styles.memberSidebarText} numberOfLines={1}>
            {MEMBER_SIDEBAR_TEXT[pricing.membershipType]}
          </Text>
        </View>
      ) : null}

      <View style={styles.imageContainer}>
        <ProductTileImage item={item} size="small" testID={`${testID}-image`} />
      </View>

      <View style={styles.contentContainer}>
        <Text style={styles.name} numberOfLines={2} testID={`${testID}-name`}>
          {item.name}
        </Text>

        {showPreviousQuantity ? (
          <View style={styles.previousQuantityRow}>
            <Text style={styles.previousQuantityText}>Previous Order Qty: </Text>
            <Text style={styles.previousQuantityText}>{item.quantity}</Text>
          </View>
        ) : null}

        {/* Bottom section: side-by-side on desktop, stacked on mobile for member tiles */}
        <View style={stackedLayout ? styles.bottomStacked : styles.bottomRow}>
          <View style={stackedLayout ? undefined : styles.priceContainer}>
            <Text style={styles.byline}>{byline}</Text>

            {hasMemberPricing ? (
              <View style={styles.dualPriceRow}>
                <View style={styles.priceSegment}>
                  <Text style={styles.priceTag}>Member </Text>
                  <Text style={styles.memberPrice} testID={`${testID}-member-price`}>
                    {toAUD(pricing.memberPrice!)}
                  </Text>
                  <Text style={styles.priceUnit}> each</Text>
                </View>
                <View style={styles.priceSegment}>
                  <Text style={styles.priceTag}>Non-members </Text>
                  <Text
                    style={[
                      styles.nonMemberPrice,
                      pricing.showMemberLabel ? styles.nonMemberPriceStruck : null,
                    ]}
                    testID={`${testID}-nonmember-price`}>
                    {toAUD(pricing.nonMemberPrice!)}
                  </Text>
                  <Text style={styles.priceUnit}> each</Text>
                </View>
              </View>
            ) : (
              <View style={styles.priceRow}>
                <Text style={styles.price} testID={`${testID}-price`}>
                  {toAUD(pricing.displayPrice)}
                </Text>
                <Text style={styles.priceUnit}> each</Text>
              </View>
            )}
          </View>

          {!isGiftCard ? (
            <View style={stackedLayout ? styles.actionsEnd : styles.actionsContainer}>
              <ProductTileActions
                item={item}
                testContext={testContext}
                showWishlistButton={false}
                fullWidth={false}
                testID={`${testID}-actions`}
              />
            </View>
          ) : null}
        </View>
      </View>
    </ProductTileContainer>
  );
});

export default ProductTileHorizontal;

// Member badge yellow — from theme, same as MembersPriceLabel
const MEMBER_YELLOW = theme.colour.background.memberPriceBadge;

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    padding: TILE_DIMENSIONS.containerPadding,
    minHeight: TILE_DIMENSIONS.horizontalMinHeight,
  },
  // Yellow border + flush sidebar: zero left/top/bottom so sidebar spans full height
  // at left edge. Keep right padding for button spacing.
  containerMember: {
    borderWidth: TILE_DIMENSIONS.memberLabelBorderWidth,
    borderColor: MEMBER_YELLOW,
    borderRadius: TILE_DIMENSIONS.borderRadius,
    paddingLeft: 0,
    paddingTop: 0,
    paddingBottom: 0,
  },
  // Yellow left sidebar — flush left, full height via alignSelf: stretch
  memberSidebar: {
    width: TILE_DIMENSIONS.horizontalMemberLabelHeight,
    backgroundColor: MEMBER_YELLOW,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'stretch',
    marginRight: TILE_DIMENSIONS.horizontalMemberLabelGap,
    overflow: 'hidden',
  },
  memberSidebarPrivate: {
    backgroundColor: 'transparent',
    borderRightWidth: TILE_DIMENSIONS.memberLabelBorderWidth,
    borderRightColor: MEMBER_YELLOW,
  },
  memberSidebarText: {
    fontSize: 8,
    fontWeight: 'bold',
    color: COLORS.nameText,
    letterSpacing: 1,
    textAlign: 'center',
    ...Platform.select({
      web: {
        // CSS writing-mode rotates text within the container's flow,
        // unlike transform:rotate which doesn't change box model.
        writingMode: 'vertical-rl' as const,
        transform: [{rotate: '180deg'}],
        whiteSpace: 'nowrap' as const,
      },
      default: {
        // Native: explicit width prevents wrapping before rotation.
        // After -90deg, this width becomes the visual height.
        width: 100,
        transform: [{rotate: '-90deg'}],
      },
    }),
  },
  imageContainer: {
    width: TILE_DIMENSIONS.horizontalImageWidth,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: TILE_DIMENSIONS.containerPadding,
  },
  contentContainer: {
    flex: 1,
    paddingLeft: TILE_DIMENSIONS.horizontalContentPaddingLeft,
    paddingVertical: TILE_DIMENSIONS.containerPadding,
    justifyContent: 'space-between',
  },
  name: {
    ...TYPOGRAPHY.nameHorizontal,
    color: COLORS.nameText,
    flex: 1,
  },
  previousQuantityRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginVertical: TILE_DIMENSIONS.horizontalPreviousQtyMarginV,
  },
  previousQuantityText: {
    ...TYPOGRAPHY.previousQuantity,
    color: COLORS.nameText,
  },
  // Desktop: pricing + actions side by side
  bottomRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  // Mobile member tiles: pricing full width, button below right-aligned
  bottomStacked: {
    flexDirection: 'column',
  },
  priceContainer: {
    flex: 1,
    overflow: 'hidden',
  },
  byline: {
    ...TYPOGRAPHY.byline,
    color: COLORS.bylineText,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  dualPriceRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: TILE_DIMENSIONS.horizontalPriceSegmentGap,
  },
  priceSegment: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  price: {
    ...TYPOGRAPHY.priceHorizontal,
    color: COLORS.priceText,
  },
  priceTag: {
    ...TYPOGRAPHY.priceUnit,
    color: COLORS.secondaryText,
  },
  memberPrice: {
    ...TYPOGRAPHY.priceHorizontal,
    color: COLORS.priceText,
  },
  nonMemberPrice: {
    ...TYPOGRAPHY.priceHorizontal,
    color: COLORS.secondaryText,
  },
  // DIS-1708: the strike claims "this is not what you pay". Only an entitled member —
  // signed in, holding a Rewards card, at a loyalty-enabled store — pays the member
  // price instead, so only that shopper sees the non-member figure struck.
  // `showMemberLabel` is the resolver's entitlement signal; `advertiseMemberPrice`
  // (which gates the pair itself) is store-scoped and says nothing about the reader.
  nonMemberPriceStruck: {
    textDecorationLine: 'line-through',
  },
  priceUnit: {
    ...TYPOGRAPHY.priceUnit,
    color: COLORS.bylineText,
  },
  actionsContainer: {
    justifyContent: 'center',
    width: 110,
  },
  // Mobile stacked: button right-aligned below pricing, same width as inline
  actionsEnd: {
    alignSelf: 'flex-end',
    width: 110,
    marginTop: 4,
  },
});
